// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file storeGuards.js
 * @description Guards para evitar corrupción del store: nunca sobrescribir con vacío/null
 * cuando la API falla o los datos son incompletos.
 */

const PREFIX = '[GUARD]';

/**
 * Decide si está permitido hacer merge con apiMessages.
 * No permitir merge cuando success=false (llamador debe no llamar).
 * Si apiMessages es null/undefined, no considerar como "datos válidos" para reemplazar realtime.
 * @param {boolean} apiSuccess - success de la respuesta API
 * @param {Array|null|undefined} apiMessages - datos recibidos
 * @param {number} cid - conversation id
 * @param {number} currentLength - cantidad actual en store para cid
 * @returns {{ allowed: boolean, reason: string }}
 */
export function canMerge(apiSuccess, apiMessages, cid, currentLength) {
  if (apiSuccess !== true) {
    if (typeof console !== 'undefined') {
      console.warn(PREFIX, 'merge rejected', { cid, reason: 'api_success_not_true', apiSuccess });
    }
    return { allowed: false, reason: 'api_success_not_true' };
  }
  if (apiMessages != null && !Array.isArray(apiMessages)) {
    if (typeof console !== 'undefined') {
      console.warn(PREFIX, 'merge rejected', { cid, reason: 'apiMessages_not_array' });
    }
    return { allowed: false, reason: 'apiMessages_not_array' };
  }
  return { allowed: true, reason: 'ok' };
}

/**
 * Decide si está permitido hacer setConversationMessages(cid, list).
 * No permitir set con null/undefined (evitar borrar por error).
 * Permitir set con [] solo si es intencional (clear) - el llamador debe usar setConversationMessages para clear.
 * @param {Array|null|undefined} list - nueva lista
 * @param {number} cid - conversation id
 * @param {number} currentLength - cantidad actual
 * @returns {{ allowed: boolean, reason: string }}
 */
export function canSet(list, cid, currentLength) {
  if (list == null) {
    if (typeof console !== 'undefined') {
      console.warn(PREFIX, 'set rejected', { cid, reason: 'list_null_undefined' });
    }
    return { allowed: false, reason: 'list_null_undefined' };
  }
  if (!Array.isArray(list)) {
    if (typeof console !== 'undefined') {
      console.warn(PREFIX, 'set rejected', { cid, reason: 'list_not_array' });
    }
    return { allowed: false, reason: 'list_not_array' };
  }
  return { allowed: true, reason: 'ok' };
}

/**
 * Valida payload WS antes de insertar en store.
 * @param {Object} payload - event.detail
 * @param {number} currentUserId - usuario actual
 * @returns {{ allowed: boolean, reason: string }}
 */
export function canAddMessageFromWs(payload, currentUserId) {
  if (payload == null || typeof payload !== 'object') {
    if (typeof console !== 'undefined') console.warn(PREFIX, 'ws rejected', { reason: 'payload_invalid' });
    return { allowed: false, reason: 'payload_invalid' };
  }
  const cid = payload.conversacion_id ?? payload.conversation_id;
  if (cid == null) {
    if (typeof console !== 'undefined') console.warn(PREFIX, 'ws rejected', { reason: 'conversation_id_missing' });
    return { allowed: false, reason: 'conversation_id_missing' };
  }
  const remitenteId = payload.remitente_id ?? payload.usuario_id ?? payload.emisor_id ?? payload.emisor;
  if (!remitenteId) {
    if (typeof console !== 'undefined') console.warn(PREFIX, 'ws rejected', { reason: 'remitente_id_missing' });
    return { allowed: false, reason: 'remitente_id_missing' };
  }
  if (Number(remitenteId) === Number(currentUserId)) {
    return { allowed: false, reason: 'own_message' };
  }
  return { allowed: true, reason: 'ok' };
}
